import { useState, useCallback, useEffect, useRef } from 'react';
import { Search, UserPlus, Cloud, Loader2 } from 'lucide-react';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { EMPLOYMENT_TYPES, EMPLOYMENT_TYPE_LABELS, WAGE_TYPES, WAGE_TYPE_LABELS } from '../../types';
import type { Department, EmploymentType, WageType, AzureUserResult, UserEntry } from '../../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DEPARTMENTS = ['SEO', 'SEA', 'Social', 'Content'] as const;

const DEPARTMENT_NAMES: Record<string, string> = {
  SEO: 'SEO',
  SEA: 'SEA',
  Social: 'Social Media',
  Content: 'Content',
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (user: UserEntry) => void;
}

type TabMode = 'manual' | 'azure';

export function CreateEmployeeDialog({ open, onClose, onCreated }: Props) {
  const [activeTab, setActiveTab] = useState<TabMode>('manual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Formular-State
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [department, setDepartment] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType | ''>('');
  const [employmentStart, setEmploymentStart] = useState('');
  const [employmentEnd, setEmploymentEnd] = useState('');
  const [personnelNumber, setPersonnelNumber] = useState('');
  const [wageType, setWageType] = useState<WageType | ''>('');
  const [monthlySalary, setMonthlySalary] = useState('');
  const [contractualHours, setContractualHours] = useState('');
  const [hourlyWage, setHourlyWage] = useState('');
  const [rv, setRv] = useState(false);
  const [hansefit, setHansefit] = useState(false);
  const [excludeFromExport, setExcludeFromExport] = useState(false);

  // Azure AD Suche
  const [azureQuery, setAzureQuery] = useState('');
  const [azureResults, setAzureResults] = useState<AzureUserResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedAzureUser, setSelectedAzureUser] = useState<AzureUserResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { createUser, searchAzureUsers, importAzureUser } = useAPIStorage();

  // M118: Unmount-Cleanup fuer den debounced Azure-Search-Timer.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // m130: Form-State beim Reopen ruecksetzen — ausschliesslich bei der
  // false → true-Transition von `open`. Sonst liefe der Reset auch bei
  // unrelated Re-Rendern wahrend der Dialog schon offen ist und wuerde
  // getippte Eingaben verwerfen.
  const prevOpenRef = useRef(false);

  const resetFormState = useCallback(() => {
    setActiveTab('manual');
    setError(null);
    setSuccess(false);
    setIsSubmitting(false);
    setEmail('');
    setFirstName('');
    setLastName('');
    setDepartment('');
    setEmploymentType('');
    setEmploymentStart('');
    setEmploymentEnd('');
    setPersonnelNumber('');
    setWageType('');
    setMonthlySalary('');
    setContractualHours('');
    setHourlyWage('');
    setRv(false);
    setHansefit(false);
    setExcludeFromExport(false);
    setAzureQuery('');
    setAzureResults([]);
    setIsSearching(false);
    setSearchError(null);
    setSelectedAzureUser(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      resetFormState();
    }
  }, [open, resetFormState]);

  // Azure AD Suche mit Debounce
  const handleAzureSearch = useCallback(
    (query: string) => {
      setAzureQuery(query);
      setSearchError(null);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (query.length < 3) {
        setAzureResults([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const results = await searchAzureUsers(query);
          setAzureResults(results);
        } catch (e) {
          setSearchError(e instanceof Error ? e.message : 'Fehler bei der Azure AD Suche');
          setAzureResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [searchAzureUsers]
  );

  // Azure User auswählen → Formularfelder vorausfüllen
  const handleSelectAzureUser = (user: AzureUserResult) => {
    setSelectedAzureUser(user);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    // Abteilung nur setzen wenn sie einem bekannten Department entspricht
    const matchedDept = DEPARTMENTS.find(
      (d) => d.toLowerCase() === (user.department || '').toLowerCase()
    );
    setDepartment(matchedDept || '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Client-seitige Validierung
    if (!email || !firstName || !lastName) {
      setError('E-Mail, Vorname und Nachname sind Pflichtfelder');
      setIsSubmitting(false);
      return;
    }

    const userData = {
      email,
      firstName,
      lastName,
      department: (department || null) as Department | null,
      employmentType: employmentType || null,
      employmentStart: employmentStart || null,
      employmentEnd: employmentEnd || null,
      personnelNumber: personnelNumber || null,
      excludeFromExport,
      wageType: wageType || null,
      hourlyWage: hourlyWage ? Number(hourlyWage) : null,
      monthlySalary: monthlySalary ? Number(monthlySalary) : null,
      contractualHours: contractualHours ? Number(contractualHours) : null,
      rv,
      hansefit,
      ...(hourlyWage ? {
        settings: {
          hourlyWage: Number(hourlyWage),
        },
      } : {}),
    };

    try {
      let newUser;

      if (activeTab === 'azure' && selectedAzureUser) {
        newUser = await importAzureUser({
          ...userData,
          azureId: selectedAzureUser.azureId,
        });
      } else {
        newUser = await createUser(userData);
      }

      setSuccess(true);
      onCreated(newUser as UserEntry);

      // Dialog nach kurzem Feedback schließen
      setTimeout(() => onClose(), 800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Anlegen des Mitarbeiters');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-4 border-b border-gray-200 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-900 rounded-lg">
              <UserPlus className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </div>
            <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              Neuen Mitarbeiter anlegen
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Formular zum Anlegen eines neuen Mitarbeiters
          </DialogDescription>
        </DialogHeader>

        {/* Tab-Leiste */}
        <div className="flex border-b border-gray-200 dark:border-white/10">
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${activeTab === 'manual'
                ? 'text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            <UserPlus className="w-4 h-4" />
            Manuell
          </button>
          <button
            onClick={() => setActiveTab('azure')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2
              ${activeTab === 'azure'
                ? 'text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            <Cloud className="w-4 h-4" />
            Aus Azure AD
          </button>
        </div>

        {/* Inhalt (scrollbar) */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Azure AD Suche (nur Tab "azure") */}
          {activeTab === 'azure' && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Name oder E-Mail suchen (mind. 3 Zeichen)..."
                  value={azureQuery}
                  onChange={(e) => handleAzureSearch(e.target.value)}
                  className="pl-10 pr-4"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>

              {searchError && (
                <p className="text-sm text-red-600 dark:text-red-400">{searchError}</p>
              )}

              {azureResults.length > 0 && (
                <ul className="border border-gray-200 dark:border-white/10 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                  {azureResults.map((user) => (
                    <li key={user.azureId}>
                      <button
                        type="button"
                        onClick={() => handleSelectAzureUser(user)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                          ${selectedAzureUser?.azureId === user.azureId
                            ? 'bg-gray-50 dark:bg-gray-900 border-l-2 border-gray-900 dark:border-gray-100'
                            : ''
                          }`}
                      >
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {user.displayName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                          {user.department && ` — ${user.department}`}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {azureQuery.length >= 3 && !isSearching && azureResults.length === 0 && !searchError && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                  Keine Ergebnisse gefunden
                </p>
              )}
            </div>
          )}

          {/* Pflichtfelder */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Pflichtfelder</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ce-firstName" className="mb-1">
                  Vorname *
                </Label>
                <Input
                  type="text"
                  id="ce-firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="ce-lastName" className="mb-1">
                  Nachname *
                </Label>
                <Input
                  type="text"
                  id="ce-lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ce-email" className="mb-1">
                E-Mail *
              </Label>
              <Input
                type="email"
                id="ce-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Stammdaten */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Stammdaten</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ce-department" className="mb-1">
                  Abteilung
                </Label>
                <Select value={department || undefined} onValueChange={(v) => setDepartment(v === '__none__' ? '' : v)}>
                  <SelectTrigger id="ce-department">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Keine Angabe</SelectItem>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>{DEPARTMENT_NAMES[dept]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ce-personnelNumber" className="mb-1">
                  Personalnummer
                </Label>
                <Input
                  type="text"
                  id="ce-personnelNumber"
                  value={personnelNumber}
                  onChange={(e) => setPersonnelNumber(e.target.value)}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ce-empType" className="mb-1">
                  Beschäftigungsart
                </Label>
                <Select value={employmentType || undefined} onValueChange={(v) => setEmploymentType(v === '__none__' ? '' : v as EmploymentType | '')}>
                  <SelectTrigger id="ce-empType">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Keine Angabe</SelectItem>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{EMPLOYMENT_TYPE_LABELS[type]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ce-empStart" className="mb-1">
                  Beschäftigt seit
                </Label>
                <Input
                  type="date"
                  id="ce-empStart"
                  value={employmentStart}
                  onChange={(e) => setEmploymentStart(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ce-empEnd" className="mb-1">
                  Beschäftigt bis
                </Label>
                <Input
                  type="date"
                  id="ce-empEnd"
                  value={employmentEnd}
                  onChange={(e) => setEmploymentEnd(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Lohn-Daten */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Lohn-Daten</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ce-wageType" className="mb-1">
                  Lohnart
                </Label>
                <Select value={wageType || undefined} onValueChange={(v) => setWageType(v === '__none__' ? '' : v as WageType | '')}>
                  <SelectTrigger id="ce-wageType">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Keine Angabe</SelectItem>
                    {WAGE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{WAGE_TYPE_LABELS[type]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {wageType === 'HOURLY' && (
                <div>
                  <Label htmlFor="ce-hourlyWage" className="mb-1">
                    Stundenlohn (€)
                  </Label>
                  <Input
                    type="number"
                    id="ce-hourlyWage"
                    min="0"
                    step="0.01"
                    value={hourlyWage}
                    onChange={(e) => setHourlyWage(e.target.value)}
                    placeholder="z.B. 13,90"
                  />
                </div>
              )}
              {wageType === 'FIXED' && (
                <>
                  <div>
                    <Label htmlFor="ce-monthlySalary" className="mb-1">
                      Monatsgehalt (€)
                    </Label>
                    <Input
                      type="number"
                      id="ce-monthlySalary"
                      min="0"
                      step="0.01"
                      value={monthlySalary}
                      onChange={(e) => setMonthlySalary(e.target.value)}
                      placeholder="z.B. 2500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ce-contractualHours" className="mb-1">
                      Vertragliche Wochenstunden
                    </Label>
                    <Input
                      type="number"
                      id="ce-contractualHours"
                      min="0"
                      max="50"
                      step="0.5"
                      value={contractualHours}
                      onChange={(e) => setContractualHours(e.target.value)}
                      placeholder="z.B. 20"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Checkboxen */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer" title="Rentenversicherungspflichtig">
              <input
                type="checkbox"
                checked={rv}
                onChange={(e) => setRv(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-white/10 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">RV-pflichtig</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title="Hansefit-Mitgliedschaft">
              <input
                type="checkbox"
                checked={hansefit}
                onChange={(e) => setHansefit(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-white/10 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">Hansefit</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title="Vom Lohn-Export ausschließen">
              <input
                type="checkbox"
                checked={excludeFromExport}
                onChange={(e) => setExcludeFromExport(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-white/10 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">Vom Export ausschließen</span>
            </label>
          </div>

          {/* Fehlermeldung */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Erfolgsmeldung */}
          {success && (
            <div className="p-3 border border-green-300/60 bg-green-50 dark:border-green-500/20 dark:bg-green-900/10 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-300">Mitarbeiter wurde erfolgreich angelegt!</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <DialogFooter className="flex-row justify-end gap-3 p-4 border-t border-gray-200 dark:border-white/10 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || success || !email || !firstName || !lastName}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Anlegen...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Mitarbeiter anlegen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
